// Module: tradesDisplay.js
// Purpose: Pure, dependency-free helpers for the Orders.jsx Trades tab —
//   (A) the Trades-tab CONSUMER sort (closed round-trip pairs first), and
//   (honest-B step 1/3) the wide pairing-window date range + the IST-calendar-day
//   display filter that scopes FIFO-paired groups back to the user's selected range.
//
//   Deliberately extracted out of Orders.jsx into a module with ZERO React/context/API
//   imports: Orders.jsx transitively imports TradingDataContext -> AuthContext -> api/auth.js
//   -> axios, and this axios version ships ESM-only (`"type": "module"` in its package.json),
//   which breaks CRA's default jest transform (transformIgnorePatterns skips node_modules)
//   for any test that imports Orders.jsx without first jest.mock()-ing that whole chain away.
//   Keeping these pure functions here lets them be unit-tested directly against the REAL
//   fixtures with no mocking scaffold at all.

//! honest-B (2026-07-06, fix/bd-trades-no-pair-no-pnl) — the BD FIFO-pairing walk is fed
//  a WIDER real order window than the Orders-table display window, so a trade whose
//  opening leg is outside the selected display range still pairs correctly instead of
//  showing as a naked "Needs reconcile" remainder. This is a LOCAL GET over already-swept
//  broker_orders rows (see services.trading.getLiveOrders -> /orders/consolidated) — NOT a
//  broker round-trip — so 120 real days is cheap. Genuinely-older openers (never swept)
//  still fall to the honest needsReconcile detector; nothing is fabricated.
export const BD_PAIRING_WINDOW_DAYS = 120;

// --> honest-B step 1 — the wide REAL pairing-input window (BD_PAIRING_WINDOW_DAYS, UTC
//   date strings — same convention as Orders.jsx's _presetToDateRange; this is a query
//   param for the server GET, not itself used for the IST display-day comparison below).
export function pairingWindowDateRange(now = new Date()) {
  const toISO = (d) => d.toISOString().split('T')[0];
  const d = new Date(now);
  d.setDate(d.getDate() - BD_PAIRING_WINDOW_DAYS);
  return { from_dt: toISO(d), to_dt: toISO(now) };
}

// --> honest-B step 3 — IST calendar-day key formatter (mirrors tradingTransforms.js's
//   internal _istDateKey / _fmtTimeIST pattern: en-CA gives a sortable YYYY-MM-DD string).
const _istDayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
});
const _istDayKey = (d) => _istDayKeyFmt.format(d);

//! honest-B step 3 (arch-critic rev1#8) — the display-filter's date range is computed
//! independently of Orders.jsx's _presetToDateRange (which is UTC-midnight-based and feeds
//! the SERVER windowed fetch). Comparing a trade's IST exit-day against a UTC-midnight
//! `to_dt` string can incorrectly drop a trade that filled TODAY in IST terms but before
//! UTC's own midnight has rolled over (IST runs 5:30 ahead of UTC). Computing both bounds
//! via the SAME IST formatter, shifted by plain calendar-day arithmetic, sidesteps that
//! mismatch entirely — "today" here always means "today in IST", never "today in UTC".
function _presetToISTDayRange(preset) {
  const now = new Date();
  const toKey = _istDayKey(now);
  const daysBack = preset === '1D' ? 0 : preset === '1W' ? 7 : preset === '1M' ? 30 : 0;
  const from = new Date(now);
  from.setDate(from.getDate() - daysBack);
  return { fromKey: _istDayKey(from), toKey };
}

/**
 * filterClosedTradesToDisplayRange — honest-B step 3. The FIFO pairing walk runs over the
 * WIDE pairing window (120d); this filters the resulting groups back down to the user's
 * selected display range (1D/1W/1M) BY EXIT DATE on the IST calendar day, inclusive.
 * OPEN groups (open positions / remainders) are ALWAYS shown — they reflect current state,
 * not a point-in-time window — never suppressed by this filter.
 * @param {Array} trades - TradeGroup[] (from groupOrdersIntoTrades)
 * @param {string} dateRangePreset - '1D' | '1W' | '1M'
 * @returns {Array} TradeGroup[] filtered
 */
export function filterClosedTradesToDisplayRange(trades, dateRangePreset) {
  const { fromKey, toKey } = _presetToISTDayRange(dateRangePreset);
  return (trades || []).filter((t) => {
    if (t.status !== 'closed') return true; // open positions/remainders: always shown
    if (!t.exitTime) return true; // no exit to key off — never hide, safer default
    const exitKey = _istDayKey(new Date(t.exitTime));
    return exitKey >= fromKey && exitKey <= toKey;
  });
}

/**
 * sortTradesForDisplay — A: Trades-tab CONSUMER sort (Orders.jsx only). Does NOT touch
 * groupOrdersIntoTrades' own §5 sort (open-first) — that stays shared/unchanged for
 * AlgoDashboard/StrategyDeepDive/MonitoringTab, which slice its output top-N and rely on
 * open-first so a live trade is never lost off the end of a short slice.
 * Here, on the Trades tab (which shows the FULL list, no slicing), closed round-trip
 * trades — real legs (P&L may be 'Unresolved' since 144-6: the browser no longer computes it,
 * the card shows the OMS lifecycle net or a named reason) — sort FIRST so the first screen is
 * completed trades,
 * not the (usually few) open positions/remainders:
 *   1. closed round-trip trades ((bracketOrders?.length||0) > 0), exitTime desc
 *   2. other closed trades (e.g. degenerate BD single-row cards), exitTime||entryTime desc
 *   3. open positions/remainders, entryTime desc
 * @param {Array} trades - TradeGroup[]
 * @returns {Array} a NEW sorted array (input is not mutated)
 */
export function sortTradesForDisplay(trades) {
  //* 144-6: a closed pair is keyed on its legs only — `pnl` is the OMS lifecycle net and is
  //  null (Unresolved) for broker-direct / pre-release pairs, which are still closed pairs.
  const isClosedPair = (t) => (t.bracketOrders?.length || 0) > 0;
  const closedPairs = [];
  const otherClosed = [];
  const opens = [];
  for (const t of trades || []) {
    if (t.status === 'open') opens.push(t);
    else if (isClosedPair(t)) closedPairs.push(t);
    else otherClosed.push(t);
  }
  closedPairs.sort((a, b) => (b.exitTime || 0) - (a.exitTime || 0));
  otherClosed.sort((a, b) => (b.exitTime || b.entryTime || 0) - (a.exitTime || a.entryTime || 0));
  opens.sort((a, b) => (b.entryTime || 0) - (a.entryTime || 0));
  return [...closedPairs, ...otherClosed, ...opens];
}
